import { GridApi } from 'ag-grid-community'
import { createInvalidValueValidationIssue, OrganisationHelper, TradingEntityHelper } from 'model'
import { Locale, Translation } from 'locale'
import {
  Collection,
  DealStatus,
  FilterOperation,
  Organisation,
  OrganisationType,
  Product,
  ProductSector,
  SearchDealStatus,
  SubscriptionFilter,
  SubscriptionIdentifier,
  TradingEntity,
  TradingRelationship,
  User,
  UserStatus,
  ValidationIssue
} from 'api'
import { uniq } from 'lodash/fp'
import { flatten, isEmpty, isEqual, isNil, isNull } from 'lodash'
import { compareInsensitive, toLocalDateString } from 'utils'
import { SubscriptionDetail } from 'store/selection/selectionTypes'
import { DateRange } from 'view/shared/entity/DateRangeSelectbox'
import { addBusinessDays, isWeekend, toDate } from 'date-fns'
import { SubscriptionDates } from './SubscriptionFilterComponents/SubscriptionDateRange'
import { isNotEmpty, isNotNil } from 'utils/predicate'

type FilterDateFields = 'createdAt' | 'tradeDate' | 'startDate' | 'endDate'

export interface SubscriptionFiltersProps {
  activeSubscriptionFilters: SubscriptionFilter[]
  subscriptionIdentifier: SubscriptionIdentifier
  gridApi?: GridApi
}

const invalidFilterDatesErrorMessage: Record<FilterDateFields, string> = {
  createdAt: Locale.translate(Translation.SC_FILTER_INVALID_CREATED_AT_DATE_RANGE),
  tradeDate: Locale.translate(Translation.SC_FILTER_INVALID_TRADE_DATE_RANGE),
  startDate: Locale.translate(Translation.SC_FILTER_INVALID_START_DATE_RANGE),
  endDate: Locale.translate(Translation.SC_FILTER_INVALID_END_DATE_RANGE)
}

export function createInvalidFilterDatesValidationIssue(afterDate: string | null, beforeDate: string | null, fieldName: FilterDateFields) {
  return afterDate && beforeDate && new Date(beforeDate) <= new Date(afterDate)
    ? createInvalidValueValidationIssue(fieldName, invalidFilterDatesErrorMessage[fieldName])
    : null
}

export const createInvalidDateRangeValuesValidationIssues = (
  subscriptionDates: SubscriptionDates,
  fieldName: string
): ValidationIssue[] => {
  const validationIssues = []
  const { firstDate, secondDate, dateRange } = subscriptionDates
  const dateRangeBetween = dateRange === DateRange.Between
  const firstDateGreaterThanOrEqualsSecondDate = !!(firstDate && secondDate && new Date(firstDate) >= new Date(secondDate))
  const secondDateIsMissing = isNull(secondDate)

  if (!secondDateIsMissing && dateRangeBetween && firstDateGreaterThanOrEqualsSecondDate) {
    validationIssues.push(createInvalidValueValidationIssue(fieldName, Locale.translate(Translation.SC_FILTER_INVALID_DATE_RANGE)))
  }
  if (dateRangeBetween && secondDateIsMissing) {
    validationIssues.push(createInvalidValueValidationIssue(fieldName, Locale.translate(Translation.SC_FILTER_SECOND_DATE_MISSING)))
  }

  return validationIssues
}

export const getBuyOrSellSideOrganisationIds = (organisations: Organisation[], buySide: boolean): string[] => {
  return organisations
    .filter(organisation => organisation.organisationTypes.includes(buySide ? OrganisationType.BUY_SIDE : OrganisationType.SELL_SIDE))
    .map(organisation => organisation.id)
}

export const getBuyOrSellSideTradingEntityIds = (
  tradingEntities: TradingEntity[],
  organisations: Organisation[],
  buySide: boolean
): string[] => {
  const buyOrSellSideOrganisationIds = getBuyOrSellSideOrganisationIds(organisations, buySide)
  return tradingEntities
    .filter(tradingEntity =>
      buyOrSellSideOrganisationIds.find(buyOrSellSideOrganisationId => buyOrSellSideOrganisationId === tradingEntity.organisationId)
    )
    .map(tradingEntity => tradingEntity.id)
}

export const getBuyOrSellSideUsernames = (users: User[], organisations: Organisation[], buySide: boolean): string[] => {
  const buyOrSellSideOrganisationIds = getBuyOrSellSideOrganisationIds(organisations, buySide)
  return users
    .filter(user => {
      return (
        user.status !== UserStatus.DELETED &&
        buyOrSellSideOrganisationIds.find(buyOrSellSideOrganisationId => buyOrSellSideOrganisationId === user.organisationId)
      )
    })
    .map(user => user.username)
    .sort((username1, username2) => compareInsensitive(username1, username2))
}

export const getSellSideOrBuySideTradingEntityIdsFromRelationships = (
  tradingRelationshipsArray: TradingRelationship[][],
  isBuySideUser: boolean
) => {
  const relationships = tradingRelationshipsArray.map(tradingRelationships =>
    tradingRelationships.map(tradingRelationship =>
      isBuySideUser ? tradingRelationship.sellTradingEntity : tradingRelationship.buyTradingEntity
    )
  )
  return uniq(flatten(relationships))
}

export const getSellSideTradingEntityIdsBelongingToOrganisation = (
  tradingEntities: TradingEntity[],
  organisationId: string,
  tradingEntityIds: string[]
) => {
  const filteredTradingEntities = tradingEntityIds.map(tradingEntityId =>
    tradingEntities.filter(tradingEntity => tradingEntity.id === tradingEntityId)
  )
  return flatten(filteredTradingEntities)
    .filter(tradingEntity => tradingEntity.organisationId === organisationId)
    .map(tradingEntity => tradingEntity.id)
}

export const getOrganisationIdsFromTradingEntityIds = (tradingEntities: TradingEntity[], tradingEntityIds: string[]) => {
  const organisationIds = tradingEntityIds.map(tradingEntityId =>
    tradingEntities.filter(tradingEntity => tradingEntity.id === tradingEntityId).map(tradingEntity => tradingEntity.organisationId)
  )
  return uniq(flatten(organisationIds))
}

export const getCollectionIdFromName = (collections: Collection[], collectionName: string) => {
  const specifiedCollection = collections.find(collection => collection.name === collectionName)
  return specifiedCollection ? specifiedCollection.id : collectionName
}

export const getCollectionNameFromId = (collections: Collection[], collectionId: string | null) => {
  return collections.find(collection => collection.id === collectionId)?.name ?? collectionId
}

export const getTradingEntityIdsBelongingToOrganisation = (tradingEntities: TradingEntity[], organisationId: string | null) => {
  return tradingEntities.filter(tradingEntity => tradingEntity.organisationId === organisationId).map(tradingEntity => tradingEntity.id)
}

export const getOrganisationIdsBelongingToTradingEntity = (tradingEntities: TradingEntity[], tradingEntityId: string) => {
  return tradingEntities.filter(tradingEntity => tradingEntity.id === tradingEntityId).map(tradingEntity => tradingEntity.organisationId)
}

export const getOrganisationIdFromTradingEntityIdsString = (tradingEntities: TradingEntity[], tradingEntityIds: string | null) => {
  const tradingEntityIdsArray = tradingEntityIds?.split(',')
  return tradingEntities.find(tradingEntity => tradingEntityIdsArray?.includes(tradingEntity.id))?.organisationId ?? null
}

export const getUsernamesBelongingToTradingEntity = (users: User[], tradingEntityId: string) => {
  return users.filter(user => user.tradingEntityDetails.some(d => d.tradingEntityId === tradingEntityId)).map(user => user.username)
}

export const getUsernamesBelongingToOrganisation = (users: User[], organisationId: string) => {
  return users.filter(user => user.organisationId === organisationId).map(user => user.username)
}

export const getProductSectorIdsBelongingToProduct = (productSectors: ProductSector[], productId: string) => {
  return productSectors.filter(productSector => productSector.productId === productId).map(productSector => productSector.id)
}

export const getProductIdsBelongingToProductSector = (productSectors: ProductSector[], productSectorId: string) => {
  return productSectors.filter(productSector => productSector.id === productSectorId).map(productSector => productSector.productId)
}

export const getProductIdFromProductSectorIdsString = (productSectors: ProductSector[], productSectorIds: string | null) => {
  const productSectorIdsArray = productSectorIds?.split(',')
  return productSectors.find(productSector => productSectorIdsArray?.includes(productSector.id))?.productId ?? null
}

export const mapSearchDealStatusToDealStatus = (searchDealStatus: string) => {
  switch (searchDealStatus) {
    case SearchDealStatus.DONE:
      return `${DealStatus.DONE},${DealStatus.COMPLETED}`
    case SearchDealStatus.CANCELLED:
      return DealStatus.CANCELLED
    case SearchDealStatus.UNASSIGNED:
      return DealStatus.UNASSIGNED
    case SearchDealStatus.ASSIGNED:
      return DealStatus.ASSIGNED
    case SearchDealStatus.UNALLOCATED:
      return DealStatus.UNALLOCATED
    case SearchDealStatus.ALLOCATED:
      return DealStatus.ALLOCATED
    case SearchDealStatus.REJECTED:
      return DealStatus.REJECTED
    case SearchDealStatus.EXPIRED_ALLOCATION:
      return DealStatus.EXPIRED_ALLOCATION
    case SearchDealStatus.CLEARING_PENDING:
      return `${DealStatus.CLEARING_NOT_SENT},${DealStatus.CLEARING_SENT},${DealStatus.CLEARING_ACKNOWLEDGED},${DealStatus.CLEARING_FAILED},${DealStatus.PREPARED}`
    case SearchDealStatus.MID_TRADE_PENDING:
      return `${DealStatus.SERVICING_WITH_INITIATOR},${DealStatus.SERVICING_WITH_RESPONDER}`

    default:
      throw new Error(`Unhandled SearchDealStatus ${searchDealStatus}`)
  }
}

export const mapDealStatusToSearchDealStatus = (dealStatus: string) => {
  switch (dealStatus) {
    case `${DealStatus.DONE},${DealStatus.COMPLETED}`:
      return SearchDealStatus.DONE
    case DealStatus.CANCELLED:
      return SearchDealStatus.CANCELLED
    case DealStatus.UNASSIGNED:
      return SearchDealStatus.UNASSIGNED
    case DealStatus.ASSIGNED:
      return SearchDealStatus.ASSIGNED
    case DealStatus.UNALLOCATED:
      return SearchDealStatus.UNALLOCATED
    case DealStatus.ALLOCATED:
      return SearchDealStatus.ALLOCATED
    case DealStatus.REJECTED:
      return SearchDealStatus.REJECTED
    case DealStatus.EXPIRED_ALLOCATION:
      return SearchDealStatus.EXPIRED_ALLOCATION
    case `${DealStatus.CLEARING_NOT_SENT},${DealStatus.CLEARING_SENT},${DealStatus.CLEARING_ACKNOWLEDGED},${DealStatus.CLEARING_FAILED},${DealStatus.PREPARED}`:
      return SearchDealStatus.CLEARING_PENDING
    case `${DealStatus.SERVICING_WITH_INITIATOR},${DealStatus.SERVICING_WITH_RESPONDER}`:
      return SearchDealStatus.MID_TRADE_PENDING
    default:
      return dealStatus
  }
}

export const getEndDateEqualsFilterValues = (subscriptionDetail: SubscriptionDetail | undefined): string[] => {
  const endDateFilter = subscriptionDetail?.filters
    ? subscriptionDetail.filters.find(subscriptionFilter =>
        subscriptionFilter.fieldName === 'endDate' && subscriptionFilter.filterOperation === FilterOperation.EQUALS
          ? subscriptionFilter.value
          : null
      )
    : null
  return [endDateFilter?.value, endDateFilter?.orFilter?.value, endDateFilter?.orFilter?.orFilter?.value].filter(isNotNil)
}

export const getTradingEntityNameById = (allTradingEntities: TradingEntity[]) => (tradingEntityId: string) => {
  const specifiedTradingEntity = allTradingEntities.find(tradingEntity => tradingEntity.id === tradingEntityId)
  return specifiedTradingEntity ? specifiedTradingEntity.publishedName : null
}

export const getOrganisationNameById = (allOrganisations: Organisation[]) => (organisationId: string) => {
  const specifiedOrganisation = allOrganisations.find(organisation => organisation.id === organisationId)
  return specifiedOrganisation ? specifiedOrganisation.legalName : null
}

export const getProductNameById = (allProducts: Product[]) => (productId: string) => {
  const specifiedProduct = allProducts.find(product => product.id === productId)
  return specifiedProduct ? specifiedProduct.name : null
}

export const getProductSectorNameById = (allProductSectors: ProductSector[]) => (productSectorId: string) => {
  const specifiedProductSector = allProductSectors.find(productSector => productSector.id === productSectorId)
  return specifiedProductSector ? specifiedProductSector.name : null
}

export const sortTradingEntities = (entityIds: string[], tradingEntities: TradingEntity[]) => {
  const selectedTradingEntities = tradingEntities.filter(entity => entityIds.includes(entity.id))

  return selectedTradingEntities
    .sort((entity1, entity2) => compareInsensitive(TradingEntityHelper.getName(entity1), TradingEntityHelper.getName(entity2)))
    .map(entity => entity.id)
}

export const sortOrganisations = (organisationIds: string[], organisations: Organisation[]) => {
  const selectedOrganisations = organisations.filter(organisation => organisationIds.includes(organisation.id))

  return selectedOrganisations
    .sort((organisation1, organisation2) =>
      compareInsensitive(OrganisationHelper.getName(organisation1), OrganisationHelper.getName(organisation2))
    )
    .map(organisation => organisation.id)
}

export const getFilterOperationFromDateRange = (dateRange: DateRange): FilterOperation => {
  switch (dateRange) {
    case DateRange.After:
      return FilterOperation.AFTER
    case DateRange.Before:
      return FilterOperation.BEFORE
    case DateRange.Equals:
      return FilterOperation.EQUALS
    default:
      return FilterOperation.EQUALS
  }
}

export const getDateRangeFromFilter = (filter: SubscriptionFilter): DateRange => {
  const firstDate = toDate(filter.value).getUTCDate()
  const secondDate = toDate(filter.andFilter?.value).getUTCDate()
  const datesAreNotEqual = firstDate !== secondDate

  if (isNotNil(filter.andFilter)) {
    return datesAreNotEqual ? DateRange.Between : DateRange.Equals
  }

  switch (filter.filterOperation) {
    case FilterOperation.AFTER:
      return DateRange.After
    case FilterOperation.BEFORE:
      return DateRange.Before
    case FilterOperation.EQUALS:
      return DateRange.Equals
    default:
      return DateRange.Equals
  }
}

export const currentAndActiveSubscriptionFiltersAreEqual = (
  currentFilters: SubscriptionFilter[],
  activeSubscriptionFilters: SubscriptionFilter[] | undefined
) => {
  return activeSubscriptionFilters ? isEqual(currentFilters, activeSubscriptionFilters) : false
}

export const dynamicDates = [
  addBusinessDays(new Date(), isWeekend(new Date()) ? 1 : 0),
  addBusinessDays(new Date(), isWeekend(new Date()) ? 2 : 1),
  addBusinessDays(new Date(), isWeekend(new Date()) ? 3 : 2)
]

export const filterEqualsDynamicDate = (currentFilterDates: string[], selectedDate: Date) => {
  return currentFilterDates.includes(toLocalDateString(selectedDate))
}

export const dynamicDateSelected = (currentFilterDates: string[]) => {
  return dynamicDates.some(dynamicDate => toLocalDateString(dynamicDate) === currentFilterDates[0])
}

export const getDynamicDateFilters = (dates: string[]) => {
  const endDateSubscriptionFilter = dates.reduce((accumulator, currentValue, index) => {
    if (index === 1) {
      return {
        fieldName: 'endDate',
        value: accumulator?.value.trim(),
        filterOperation: FilterOperation.EQUALS,
        orFilter: {
          fieldName: 'endDate',
          value: currentValue?.trim(),
          filterOperation: FilterOperation.EQUALS,
          orFilter: null,
          andFilter: null,
          calculatedValueType: null
        },
        andFilter: null,
        calculatedValueType: null
      }
    }

    if (index === 2) {
      return {
        fieldName: 'endDate',
        value: accumulator?.value.trim(),
        filterOperation: FilterOperation.EQUALS,
        orFilter: {
          fieldName: 'endDate',
          value: accumulator.orFilter?.value.trim(),
          filterOperation: FilterOperation.EQUALS,
          orFilter: {
            fieldName: 'endDate',
            value: currentValue?.trim(),
            filterOperation: FilterOperation.EQUALS,
            orFilter: null,
            andFilter: null,
            calculatedValueType: null
          },
          andFilter: null,
          calculatedValueType: null
        },
        andFilter: null,
        calculatedValueType: null
      }
    }

    return {
      fieldName: 'endDate',
      value: currentValue?.trim(),
      filterOperation: FilterOperation.EQUALS,
      orFilter: null,
      andFilter: null,
      calculatedValueType: null
    }
  }, {} as SubscriptionFilter)

  return isNotEmpty(dates) ? [endDateSubscriptionFilter] : []
}

export const removeOldSubscriptionFilter = (
  subscriptionFilters: SubscriptionFilter[],
  fieldName: string,
  filterOperation?: FilterOperation
) => {
  const fieldNameFilter = subscriptionFilters.filter(subscriptionFilter => subscriptionFilter.fieldName !== fieldName)
  const fieldNameOrFilterOperationFilter = subscriptionFilters.filter(
    subscriptionFilter => subscriptionFilter.fieldName !== fieldName || subscriptionFilter.filterOperation !== filterOperation
  )
  return filterOperation ? fieldNameOrFilterOperationFilter : fieldNameFilter
}

export const getUpdatedSubscriptionFilters = (
  subscriptionFilters: SubscriptionFilter[],
  fieldName: string,
  value: any,
  filterOperation: FilterOperation,
  orFilter?: SubscriptionFilter
): SubscriptionFilter[] => {
  if (isNull(value) || isEmpty(value)) {
    return removeOldSubscriptionFilter(subscriptionFilters, fieldName, filterOperation)
  } else {
    const trimmedValue = typeof value === 'string' ? value.trim() : value
    return [
      ...removeOldSubscriptionFilter(subscriptionFilters, fieldName, filterOperation),
      {
        fieldName: fieldName,
        value: trimmedValue,
        filterOperation: filterOperation,
        orFilter: orFilter || null,
        andFilter: null,
        calculatedValueType: null
      }
    ]
  }
}

export const getStartOfDayDateString = (date: string | null) => {
  return date ? `${date}T00:00:00.000Z` : null
}

export const getEndOfDayDateString = (date: string | null) => {
  return date ? `${date}T23:59:00.000Z` : null
}

export const getDateRangeAfterAndBeforeFilter = (fieldName: string, firstDate: string | null, secondDate: string | null) => {
  return {
    fieldName: fieldName,
    value: firstDate,
    filterOperation: FilterOperation.AFTER,
    orFilter: null,
    calculatedValueType: null,
    andFilter: {
      fieldName: fieldName,
      value: secondDate,
      filterOperation: FilterOperation.BEFORE_OR_EQUAL,
      orFilter: null,
      andFilter: null,
      calculatedValueType: null
    }
  }
}

export const getDateRangeSubscriptionFilters = (
  subscriptionDates: SubscriptionDates,
  fieldName: string,
  subscriptionFilters: SubscriptionFilter[],
  isDateAndTimeFilter = false
): SubscriptionFilter[] => {
  const dateRange = subscriptionDates.dateRange
  const firstDate = subscriptionDates.firstDate
  const secondDate = subscriptionDates.secondDate

  if (dateRange === DateRange.Between) {
    if (isNil(firstDate) && isNil(secondDate)) {
      return removeOldSubscriptionFilter(subscriptionFilters, fieldName)
    } else {
      const afterDate = isDateAndTimeFilter ? getStartOfDayDateString(firstDate) : firstDate
      const beforeDate = isDateAndTimeFilter ? getEndOfDayDateString(secondDate) : secondDate
      return [
        ...removeOldSubscriptionFilter(subscriptionFilters, fieldName),
        getDateRangeAfterAndBeforeFilter(fieldName, afterDate, beforeDate)
      ]
    }
  } else {
    if (isNil(firstDate)) {
      return removeOldSubscriptionFilter(subscriptionFilters, fieldName)
    } else {
      if (dateRange === DateRange.Equals && isDateAndTimeFilter) {
        return [
          ...removeOldSubscriptionFilter(subscriptionFilters, fieldName),
          getDateRangeAfterAndBeforeFilter(fieldName, getStartOfDayDateString(firstDate), getEndOfDayDateString(firstDate))
        ]
      } else {
        return [
          ...removeOldSubscriptionFilter(subscriptionFilters, fieldName),
          {
            fieldName: fieldName,
            value: firstDate,
            filterOperation: getFilterOperationFromDateRange(dateRange),
            orFilter: null,
            andFilter: null,
            calculatedValueType: null
          }
        ]
      }
    }
  }
}

export const getSubscriptionFilterValue = (
  subscriptionFilters: SubscriptionFilter[],
  fieldName: string,
  filterOperation?: FilterOperation
) => {
  const fieldNameValue = subscriptionFilters.find(subscriptionFilter => subscriptionFilter.fieldName === fieldName)?.value
  const fieldNameAndOperationValue = subscriptionFilters.find(
    subscriptionFilter => subscriptionFilter.fieldName === fieldName && subscriptionFilter.filterOperation === filterOperation
  )?.value

  return filterOperation ? fieldNameAndOperationValue : fieldNameValue
}

export const getDateRangeSubscriptionFilterValue = (subscriptionFilters: SubscriptionFilter[], fieldName: string): SubscriptionDates => {
  const removeTime = (stringDate: string) => stringDate.split('T')[0]

  const subscriptionFilter = subscriptionFilters.find(subscriptionFilter => subscriptionFilter.fieldName === fieldName)
  const firstDate = subscriptionFilter?.value ? toLocalDateString(removeTime(subscriptionFilter.value)) : null
  const secondDate = subscriptionFilter?.andFilter?.value ? toLocalDateString(removeTime(subscriptionFilter.andFilter.value)) : null

  return {
    firstDate: firstDate,
    secondDate: secondDate,
    dateRange: subscriptionFilter ? getDateRangeFromFilter(subscriptionFilter) : DateRange.Equals
  }
}
